import { useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Icon, Spinner } from '@iwoca/orion';
import classnames from 'classnames';
import { capitalize, get, startCase } from 'lodash';
import { connect } from 'react-redux';

import { RootState } from '@src/reducers';

import {
    ACTIVATE_SAVE_APPLICATION,
    SAVE_APPLICATION_RESET
} from './SaveApplication.actions';
import messages from './SaveApplication.messages';
import styles from './SaveApplication.module.css';
import { getAmplitudeDefaultEventProperties } from '../../tracking/amplitudeEvents';
import IwFormattedMessage from '../IwFormattedMessage/IwFormattedMessage';

const mapStateToProps = (state: RootState) => ({
    loading: get(state, 'saveApplication.loading'),
    success: get(state, 'saveApplication.success'),
    ui: state.ui
});

interface SaveApplicationLinkProps {
    loading: boolean;
    success: boolean;
    saveApplication: (activeStep: string) => { type: string };
    resetSaveApplication: () => void;
    ui: RootState;
}

const mapDispatchToProps = {
    saveApplication: (activeStep: string) => {
        tracking.ampli.saveApplicationClicked({
            'Active step': capitalize(startCase(activeStep)),
            ...getAmplitudeDefaultEventProperties()
        });

        return { type: ACTIVATE_SAVE_APPLICATION };
    },

    resetSaveApplication: () => ({ type: SAVE_APPLICATION_RESET })
};

const SaveApplicationLink = ({
    loading,
    success,
    saveApplication,
    resetSaveApplication,
    ui
}: SaveApplicationLinkProps) => {
    useEffect(() => {
        let timer: NodeJS.Timeout | undefined;

        if (success) {
            timer = setTimeout(() => {
                resetSaveApplication();
            }, 5000);
        }

        // a cleanup function
        return () => clearTimeout(timer);
    }, [success, resetSaveApplication]);

    const buttonMessage = loading
        ? messages.savingLoading
        : success
        ? messages.saveSuccess
        : messages.saveProgress;

    const isDisabled = loading || success;

    return (
        <>
            <button
                className={styles.SaveApplicationLink}
                onClick={() => saveApplication(ui?.activeStep?.id)}
                disabled={isDisabled}
                data-testid="save-progress-button">
                <div
                    className={classnames(styles.saveApplicationContainer, {
                        [styles.disabledContainerClass]: isDisabled
                    })}>
                    <IwFormattedMessage
                        {...buttonMessage}
                        className={styles.saveLinkText}
                    />

                    {loading ? (
                        <div
                            data-testid="saving-progress-icon"
                            className={styles.savingProgressContainer}>
                            <Spinner />
                        </div>
                    ) : success ? (
                        <Icon
                            icon="checkCircleOutline"
                            className={styles.saveSuccessIcon}
                            data-testid="save-success-icon"
                        />
                    ) : (
                        <Icon
                            icon="save"
                            className={styles.saveIcon}
                            data-testid="save-icon"
                        />
                    )}
                </div>
            </button>
        </>
    );
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(SaveApplicationLink);
